/** Operaciones de una columna calculada: dos columnas de la misma fila, sin otra consulta. */
export const CALCULATED_COLUMN_OPS = [
  { value: 'add', label: 'Suma' },
  { value: 'multiply', label: 'Multiplicación' },
  { value: 'concat', label: 'Concatenar' },
];

const OPS = new Set(CALCULATED_COLUMN_OPS.map((op) => op.value));
const CALC_KEY_RE = /^calc_[A-Za-z0-9_]+$/;

/** Clave de la columna en la fila. Siempre empieza con calc_ para no mezclarla con una columna de la consulta. */
export function calculatedColumnKey(raw) {
  const explicit = String(raw?.key || '').trim();
  if (CALC_KEY_RE.test(explicit)) return explicit.slice(0, 48);
  const id = String(raw?.id || raw?.label || 'col')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^_+/, '')
    .slice(0, 32) || 'col';
  return `calc_${id}`.slice(0, 48);
}

export function isCalculatedColumnKey(key) {
  return CALC_KEY_RE.test(String(key || ''));
}

function finiteNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value == null || value === '') return null;
  const n = parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

function tidyNumber(n) {
  if (Number.isInteger(n)) return n;
  return Math.round(n * 1e10) / 1e10;
}

/** 0–10, o null si no hay formato de decimales. */
export function normalizeDecimalPlaces(value) {
  if (value == null || value === '') return null;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.min(10, n);
}

function numericFormatFields(raw, op) {
  if (op === 'concat') return {};
  const fields = {};
  if (raw?.round === true) fields.round = true;
  const decimalPlaces = normalizeDecimalPlaces(raw?.decimalPlaces);
  if (decimalPlaces != null) fields.decimalPlaces = decimalPlaces;
  if (raw?.percent === true) fields.percent = true;
  return fields;
}

/** Redondeo a entero, o a la cantidad de decimales pedida.
 *  Con porcentaje el valor guardado sigue siendo el resultado; el ×100 es solo de display. */
function applyNumericFormat(n, def) {
  if (def?.percent) return tidyNumber(n);
  const decimalPlaces = normalizeDecimalPlaces(def?.decimalPlaces);
  if (decimalPlaces != null) return tidyNumber(Number(n.toFixed(decimalPlaces)));
  if (def?.round) return Math.round(n);
  return tidyNumber(n);
}

/** Texto de la celda. Con decimales conserva los ceros finales (1.5 → "1.50").
 *  Porcentaje muestra el valor × 100 (0.153 → "15.3%"). */
export function formatCalculatedDisplay(value, def) {
  if (value == null || value === '') return '';
  if (!def || def.op === 'concat') return String(value);
  const decimalPlaces = normalizeDecimalPlaces(def.decimalPlaces);
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(n)) return String(value);
  const shown = def.percent ? n * 100 : n;
  let text;
  if (decimalPlaces != null) text = shown.toFixed(decimalPlaces);
  else if (def.round) text = String(Math.round(shown));
  else text = String(tidyNumber(shown));
  return def.percent ? `${text}%` : text;
}

/**
 * Deja solo definiciones utilizables.
 * sourceKeys (opcional): si viene, descarta operandos que no están en la fila
 * y un nombre que pise una columna de la consulta.
 */
export function normalizeCalculatedColumns(list, sourceKeys) {
  if (!Array.isArray(list)) return [];
  const known = Array.isArray(sourceKeys) && sourceKeys.length ? new Set(sourceKeys) : null;
  const used = new Set();
  const out = [];
  list.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const label = String(raw.label || '').trim();
    const left = String(raw.left || '').trim();
    const right = String(raw.right || '').trim();
    const op = raw.op;
    const key = calculatedColumnKey(raw);
    if (!label || !left || !right || !OPS.has(op)) return;
    if (used.has(key) || used.has(label)) return;
    if (known && (known.has(key) || !known.has(left) || !known.has(right))) return;
    used.add(key);
    used.add(label);
    out.push({
      id: String(raw.id || key),
      key,
      label,
      left,
      op,
      right,
      ...numericFormatFields(raw, op),
    });
  });
  return out;
}

export function computeCalculatedValue(row, def) {
  const a = row?.[def.left];
  const b = row?.[def.right];
  if (def.op === 'concat') {
    return `${a == null ? '' : String(a)}${b == null ? '' : String(b)}`;
  }
  const na = finiteNumber(a);
  const nb = finiteNumber(b);
  if (na == null || nb == null) return null;
  return applyNumericFormat(def.op === 'multiply' ? na * nb : na + nb, def);
}

/** Una sola fila con la suma, para un KPI que muestra el primer valor. */
export function sumCalculatedColumn(rows, key) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length <= 1) return list;
  const sum = list.reduce((acc, row) => {
    const raw = row?.[key];
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').trim());
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
  return [{ ...(list[0] || {}), [key]: tidyNumber(sum) }];
}

/** Copia las filas y agrega cada columna calculada. No muta las filas originales. */
export function applyCalculatedColumns(rows, defs) {
  const valid = normalizeCalculatedColumns(defs);
  if (!valid.length || !Array.isArray(rows) || rows.length === 0) return rows;
  return rows.map((row) => {
    const next = { ...row };
    valid.forEach((def) => {
      next[def.key] = computeCalculatedValue(next, def);
    });
    return next;
  });
}
